// Demo transactions for testing cancel and amend flows
// Based on the transaction rules specification

export interface DemoTransaction {
  id: string
  mtoName: string
  mtoRef: string
  bankRef: string
  branchCode: string
  agentCode: string
  payoutType: 'FT' | 'IBFT' | 'OTC'
  amountFCY: string
  currencyFCY: string
  amountPKR: string
  corridor: string
  purposeCode: string
  purposeText: string
  currentState: string
  lifecycleStatus: string
  createdAt: string
  updatedAt: string
  remitterName: string
  beneficiaryName: string
  beneficiaryIBAN: string
  beneficiaryBankName?: string
  amendAllowed: boolean
  cancelAllowed: boolean
  cancellationAction: string
  amendStatus: 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED'
  cancelStatus: 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED'
  railRefs: any
  stateHistory: any[]
  ePRCNo?: string
  ePRCDate?: string
  amendmentHistory?: {
    timestamp: string
    oldAccount: string
    newAccount: string
    fetchedTitle: string
    status: 'PENDING' | 'APPROVED' | 'REJECTED'
    requestedBy: string
    checkerComment?: string
  }[]
  manualSettlementHistory?: {
    action: 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'RESUBMITTED' | 'DISCARDED'
    reason: string
    by: string
    at: string
  }[]
  manualActionComment?: {
    actionType: 'MANUAL_FAIL' | 'MANUAL_COMPLETE'
    comment: string
    timestamp: string
  }
}

const VALID_MTOS = [
  'GTBank Ghana',
  'Paga Ghana',
  'Flutterwave Nigeria',
  'M-Pesa Kenya',
]
const VALID_SUB_AGENTS = [
  'PAGA-GH-001',
  'ECO-NG-001',
  'FLW-NG-001',
  'MPESA-KE-001',
]

export const DEMO_TRANSACTIONS: DemoTransaction[] = [
  // OTC Transaction - DOWNLOAD (Failed/Inprogress) - Amend Yes, Cancel Yes
  {
    id: 'TXN-DEMO-001',
    mtoName: 'GTBank Ghana',
    mtoRef: 'MTO-DEMO-001',
    bankRef: 'BNK-DEMO-001',
    branchCode: 'GH-ACC-001',
    agentCode: 'PAGA-GH-001',
    payoutType: 'OTC',
    amountFCY: '500.00',
    currencyFCY: 'GBP',
    amountPKR: '7500',
    corridor: 'GH-UK',
    purposeCode: 'FAM',
    purposeText: 'Personal Remittance',
    currentState: 'DOWNLOAD',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 7200000).toISOString(),
    updatedAt: new Date(Date.now() - 3600000).toISOString(),
    remitterName: 'Kwame Asante',
    beneficiaryName: 'SAMUEL MENSAH',
    beneficiaryIBAN: 'GB29NWBK60161331926819',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [],
    ePRCNo: undefined,
    ePRCDate: undefined,
  },
  // OTC Transaction - SCREENING (Inprogress) - Amend Yes, Cancel Yes
  {
    id: 'TXN-DEMO-002',
    mtoName: 'Paga Ghana',
    mtoRef: 'MTO-DEMO-002',
    bankRef: 'BNK-DEMO-002',
    branchCode: 'NG-LAG-001',
    agentCode: 'ECO-NG-001',
    payoutType: 'OTC',
    amountFCY: '750.00',
    currencyFCY: 'USD',
    amountPKR: '1125000',
    corridor: 'NG-US',
    purposeCode: 'SAL',
    purposeText: 'Business Payment',
    currentState: 'SCREENING',
    lifecycleStatus: 'IN_PROGRESS',
    createdAt: new Date(Date.now() - 5400000).toISOString(),
    updatedAt: new Date(Date.now() - 1800000).toISOString(),
    remitterName: 'Adekunle Okoro',
    beneficiaryName: 'DAVID JOHNSON',
    beneficiaryIBAN: 'US021000021123456789',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [],
    ePRCNo: undefined,
    ePRCDate: undefined,
  },
  // OTC Transaction - TITLE_FETCH (Failed) - Amend Yes, Cancel Yes
  {
    id: 'TXN-DEMO-003',
    mtoName: 'Flutterwave Nigeria',
    mtoRef: 'MTO-DEMO-003',
    bankRef: 'BNK-DEMO-003',
    branchCode: 'SN-DKR-001',
    agentCode: 'FLW-NG-001',
    payoutType: 'OTC',
    amountFCY: '1000.00',
    currencyFCY: 'EUR',
    amountPKR: '655000',
    corridor: 'SN-FR',
    purposeCode: 'EDU',
    purposeText: 'Education',
    currentState: 'TITLE_FETCH',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 10800000).toISOString(),
    updatedAt: new Date(Date.now() - 7200000).toISOString(),
    remitterName: 'Fatoumata Ndiaye',
    beneficiaryName: 'MARIE DUPONT',
    beneficiaryIBAN: 'FR7630006000011234567890189',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'PENDING',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [],
    ePRCNo: undefined,
    ePRCDate: undefined,
  },
  // LFT Transaction - DOWNLOAD (Failed) - Amend Yes, Cancel Yes
  {
    id: 'TXN-DEMO-004',
    mtoName: 'M-Pesa Kenya',
    mtoRef: 'MTO-DEMO-004',
    bankRef: 'BNK-DEMO-004',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    amountFCY: '600.00',
    currencyFCY: 'EUR',
    amountPKR: '93000',
    corridor: 'KE-DE',
    purposeCode: 'MED',
    purposeText: 'Medical',
    currentState: 'DOWNLOAD',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 14400000).toISOString(),
    updatedAt: new Date(Date.now() - 10800000).toISOString(),
    remitterName: 'Grace Kipchoge',
    beneficiaryName: 'KLAUS SCHMIDT',
    beneficiaryIBAN: 'DE89370400440532013000',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [],
    ePRCNo: undefined,
    ePRCDate: undefined,
  },
  // LFT Transaction - BALANCE_INQUIRY (Inprogress) - Amend Yes, Cancel Yes
  {
    id: 'TXN-DEMO-005',
    mtoName: 'GTBank Ghana',
    mtoRef: 'MTO-DEMO-005',
    bankRef: 'BNK-DEMO-005',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    amountFCY: '850.00',
    currencyFCY: 'GBP',
    amountPKR: '12750',
    corridor: 'GH-UK',
    purposeCode: 'FAM',
    purposeText: 'Personal Remittance',
    currentState: 'BALANCE_CHECK',
    lifecycleStatus: 'IN_PROGRESS',
    createdAt: new Date(Date.now() - 3600000).toISOString(),
    updatedAt: new Date(Date.now() - 1800000).toISOString(),
    remitterName: 'Amara Jalloh',
    beneficiaryName: 'MICHAEL BROWN',
    beneficiaryIBAN: 'GB29NWBK60161331926820',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'APPROVED',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [],
    ePRCNo: undefined,
    ePRCDate: undefined,
  },
  // Payment (internal FT) - Failed - Amend No, Cancel Yes
  {
    id: 'TXN-DEMO-006',
    mtoName: 'Paga Ghana',
    mtoRef: 'MTO-DEMO-006',
    bankRef: 'BNK-DEMO-006',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    amountFCY: '1200.00',
    currencyFCY: 'USD',
    amountPKR: '1800000',
    corridor: 'NG-US',
    purposeCode: 'SAL',
    purposeText: 'Business Payment',
    currentState: 'FT_POST',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 18000000).toISOString(),
    updatedAt: new Date(Date.now() - 14400000).toISOString(),
    remitterName: 'Chinedu Eze',
    beneficiaryName: 'ANNA WILLIAMS',
    beneficiaryIBAN: 'US021000021123456790',
    amendAllowed: false,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [],
    ePRCNo: undefined,
    ePRCDate: undefined,
  },
  // IBFT Transaction - DOWNLOAD (Failed) - Amend Yes, Cancel Yes
  {
    id: 'TXN-DEMO-007',
    mtoName: 'Flutterwave Nigeria',
    mtoRef: 'MTO-DEMO-007',
    bankRef: 'BNK-DEMO-007',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    amountFCY: '950.00',
    currencyFCY: 'EUR',
    amountPKR: '622250',
    corridor: 'SN-FR',
    purposeCode: 'EDU',
    purposeText: 'Education',
    currentState: 'DOWNLOAD',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 21600000).toISOString(),
    updatedAt: new Date(Date.now() - 18000000).toISOString(),
    remitterName: 'Samuel Mensah',
    beneficiaryName: 'ROBERT TAYLOR',
    beneficiaryIBAN: 'FR7630006000011234567890190',
    beneficiaryBankName: 'Ecobank Ghana',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'PENDING',
    railRefs: {},
    stateHistory: [],
    ePRCNo: undefined,
    ePRCDate: undefined,
  },
  // IBFT Transaction - BALANCE_INQUIRY (Inprogress) - Amend Yes, Cancel Yes
  {
    id: 'TXN-DEMO-008',
    mtoName: 'M-Pesa Kenya',
    mtoRef: 'MTO-DEMO-008',
    bankRef: 'BNK-DEMO-008',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    amountFCY: '700.00',
    currencyFCY: 'EUR',
    amountPKR: '108500',
    corridor: 'KE-DE',
    purposeCode: 'MED',
    purposeText: 'Medical',
    currentState: 'BALANCE_CHECK',
    lifecycleStatus: 'IN_PROGRESS',
    createdAt: new Date(Date.now() - 7200000).toISOString(),
    updatedAt: new Date(Date.now() - 3600000).toISOString(),
    remitterName: 'Tsitsi Mwangi',
    beneficiaryName: 'JAMES SMITH',
    beneficiaryIBAN: 'DE89370400440532013001',
    beneficiaryBankName: 'Ecobank Nigeria',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'REJECTED',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [],
    ePRCNo: undefined,
    ePRCDate: undefined,
  },
  // IBFT Transaction - PAYMENT (RAAST/1LINK) Failed - Amend No, Cancel Yes
  {
    id: 'TXN-DEMO-009',
    mtoName: 'GTBank Ghana',
    mtoRef: 'MTO-DEMO-009',
    bankRef: 'BNK-DEMO-009',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    amountFCY: '1100.00',
    currencyFCY: 'GBP',
    amountPKR: '16500',
    corridor: 'GH-UK',
    purposeCode: 'FAM',
    purposeText: 'Personal Remittance',
    currentState: 'RAAST_POST',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 25200000).toISOString(),
    updatedAt: new Date(Date.now() - 21600000).toISOString(),
    remitterName: 'Thato Malatsi',
    beneficiaryName: 'JOHN DOE',
    beneficiaryIBAN: 'GB29NWBK60161331926821',
    beneficiaryBankName: 'Ecobank Kenya',
    amendAllowed: false,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'APPROVED',
    railRefs: {},
    stateHistory: [],
    ePRCNo: undefined,
    ePRCDate: undefined,
  },
  // RTGS Transaction - Failed - Amend No, Cancel Yes
  {
    id: 'TXN-DEMO-010',
    mtoName: 'Paga Ghana',
    mtoRef: 'MTO-DEMO-010',
    bankRef: 'BNK-DEMO-010',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    amountFCY: '2000.00',
    currencyFCY: 'USD',
    amountPKR: '3000000',
    corridor: 'NG-US',
    purposeCode: 'SAL',
    purposeText: 'Business Payment',
    currentState: 'RTGS_POST',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 28800000).toISOString(),
    updatedAt: new Date(Date.now() - 25200000).toISOString(),
    remitterName: 'Kwame Asante',
    beneficiaryName: 'SAMUEL MENSAH',
    beneficiaryIBAN: 'US021000021123456791',
    amendAllowed: false,
    cancelAllowed: true,
    cancellationAction: 'REVERSE_FINANCIAL_ACTION',
    amendStatus: 'NONE',
    cancelStatus: 'REJECTED',
    railRefs: {},
    stateHistory: [],
    ePRCNo: undefined,
    ePRCDate: undefined,
  },
  // SUCCESS Transaction - PAID - Amend No, Cancel No
  {
    id: 'TXN-DEMO-011',
    mtoName: 'Flutterwave Nigeria',
    mtoRef: 'MTO-DEMO-011',
    bankRef: 'BNK-DEMO-011',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    amountFCY: '800.00',
    currencyFCY: 'EUR',
    amountPKR: '524000',
    corridor: 'SN-FR',
    purposeCode: 'EDU',
    purposeText: 'Education',
    currentState: 'SUCCESS',
    lifecycleStatus: 'SUCCESS',
    createdAt: new Date(Date.now() - 32400000).toISOString(),
    updatedAt: new Date(Date.now() - 28800000).toISOString(),
    remitterName: 'Adekunle Okoro',
    beneficiaryName: 'DAVID JOHNSON',
    beneficiaryIBAN: 'FR7630006000011234567890191',
    amendAllowed: false,
    cancelAllowed: false,
    cancellationAction: 'CANNOT_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {
      RRN: 'RRN000000000011',
      STAN: '000011',
    },
    stateHistory: [],
    ePRCNo: 'EPRC-DEMO-011',
    ePRCDate: new Date(Date.now() - 28800000).toISOString(),
  },

  // NEW: FT Transaction - TITLE_FETCH (Failed) - Amend Yes
  {
    id: 'TXN-DEMO-012',
    mtoName: 'GTBank Ghana',
    mtoRef: 'MTO-DEMO-012',
    bankRef: 'BNK-DEMO-012',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    amountFCY: '1500.00',
    currencyFCY: 'GBP',
    amountPKR: '22500',
    corridor: 'GH-UK',
    purposeCode: 'FAM',
    purposeText: 'Personal Remittance',
    currentState: 'TITLE_FETCH',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 3600000).toISOString(),
    updatedAt: new Date(Date.now() - 1800000).toISOString(),
    remitterName: 'Fatoumata Ndiaye',
    beneficiaryName: 'Unknown Title',
    beneficiaryIBAN: 'GB29NWBK60161331926822',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'TITLE_FETCH',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 1800000).toISOString(),
        primaryRef: 'TITLE_FETCH-012',
        attempts: 1,
        failReasonCode: 'INVALID_ACCOUNT',
      },
    ],
  },
  // NEW: IBFT Transaction - TITLE_FETCH (Failed) - Amend Yes with History
  {
    id: 'TXN-DEMO-013',
    mtoName: 'Paga Ghana',
    mtoRef: 'MTO-DEMO-013',
    bankRef: 'BNK-DEMO-013',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    amountFCY: '250.00',
    currencyFCY: 'USD',
    amountPKR: '375000',
    corridor: 'NG-US',
    purposeCode: 'SAL',
    purposeText: 'Business Payment',
    currentState: 'TITLE_FETCH',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 86400000).toISOString(),
    updatedAt: new Date(Date.now() - 43200000).toISOString(),
    remitterName: 'Grace Kipchoge',
    beneficiaryName: 'Incorrect Name',
    beneficiaryIBAN: 'US021000021123456792',
    beneficiaryBankName: 'Ecobank Senegal',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'REJECTED',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'TITLE_FETCH',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 43200000).toISOString(),
        primaryRef: 'TITLE_FETCH-013',
        attempts: 2,
        failReasonCode: 'TITLE_MISMATCH',
      },
    ],
    amendmentHistory: [
      {
        timestamp: new Date(Date.now() - 60000000).toISOString(),
        oldAccount: 'US021000021123456792',
        newAccount: 'US021000021123456793',
        fetchedTitle: 'MARIE DUPONT',
        status: 'REJECTED',
        requestedBy: 'Maker User 1',
        checkerComment: 'Title does not match beneficiary name exactly.',
      },
    ],
  },
  // NEW: FT Transaction - RTGS_POST (Failed) - Manual Action Comment
  {
    id: 'TXN-DEMO-014',
    mtoName: 'M-Pesa Kenya',
    mtoRef: 'MTO-DEMO-014',
    bankRef: 'BNK-DEMO-014',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    amountFCY: '5000.00',
    currencyFCY: 'EUR',
    amountPKR: '775000',
    corridor: 'KE-DE',
    purposeCode: 'EDU',
    purposeText: 'Education',
    currentState: 'RTGS_POST',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 172800000).toISOString(),
    updatedAt: new Date(Date.now() - 86400000).toISOString(),
    remitterName: 'Amara Jalloh',
    beneficiaryName: 'University of Berlin',
    beneficiaryIBAN: 'DE89370400440532013002',
    amendAllowed: false,
    cancelAllowed: true,
    cancellationAction: 'REVERSE_FINANCIAL_ACTION',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'RTGS_POST',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 86400000).toISOString(),
        primaryRef: 'RTGS_POST-014',
        attempts: 1,
        failReasonCode: 'SYSTEM_TIMEOUT',
      },
    ],
    manualActionComment: {
      actionType: 'MANUAL_FAIL',
      comment:
        'RTGS system was down during processing window. Marking as failed manually.',
      timestamp: new Date(Date.now() - 80000000).toISOString(),
    },
  },

  // ===== NEW DEMO TRANSACTIONS FOR AMENDMENT HISTORY & RTGS COMMENTS =====

  // TXN-DEMO-015: FT - SUCCESS after amendment (approved) - shows old/new values in txn screen
  {
    id: 'TXN-DEMO-015',
    mtoName: 'GTBank Ghana',
    mtoRef: 'MTO-DEMO-015',
    bankRef: 'BNK-DEMO-015',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    amountFCY: '2500.00',
    currencyFCY: 'GBP',
    amountPKR: '37500',
    corridor: 'GH-UK',
    purposeCode: 'FAM',
    purposeText: 'Personal Remittance',
    currentState: 'SUCCESS',
    lifecycleStatus: 'SUCCESS',
    createdAt: new Date(Date.now() - 259200000).toISOString(),
    updatedAt: new Date(Date.now() - 172800000).toISOString(),
    remitterName: 'Chinedu Eze',
    beneficiaryName: 'KLAUS SCHMIDT',
    beneficiaryIBAN: 'GB29NWBK60161331926823',
    amendAllowed: false,
    cancelAllowed: false,
    cancellationAction: 'CANNOT_CANCEL',
    amendStatus: 'APPROVED',
    cancelStatus: 'NONE',
    railRefs: {
      RRN: 'RRN000000000015',
      STAN: '000015',
    },
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 250000000).toISOString(),
        primaryRef: 'SCR-015',
        durationMs: 1100,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 248000000).toISOString(),
        primaryRef: 'TF-015-FAIL',
        failReasonCode: 'INVALID_ACCOUNT',
        durationMs: 900,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(Date.now() - 200000000).toISOString(),
        primaryRef: 'TF-015-PASS',
        durationMs: 750,
        attempts: 1,
      },
      {
        stateCode: 'BALANCE_CHECK',
        status: 'PASS',
        timestamp: new Date(Date.now() - 198000000).toISOString(),
        primaryRef: 'BC-015',
        durationMs: 400,
        attempts: 1,
      },
      {
        stateCode: 'FT_POST',
        status: 'PASS',
        timestamp: new Date(Date.now() - 195000000).toISOString(),
        primaryRef: 'RRN000000000015',
        durationMs: 2100,
        attempts: 1,
      },
      {
        stateCode: 'SUCCESS',
        status: 'PASS',
        timestamp: new Date(Date.now() - 172800000).toISOString(),
        primaryRef: 'EPRC-015',
        durationMs: 80,
        attempts: 1,
      },
    ],
    ePRCNo: 'EPRC-DEMO-015',
    ePRCDate: new Date(Date.now() - 172800000).toISOString(),
    amendmentHistory: [
      {
        timestamp: new Date(Date.now() - 240000000).toISOString(),
        oldAccount: 'GB29NWBK60161331926824',
        newAccount: 'GB29NWBK60161331926823',
        fetchedTitle: 'KLAUS SCHMIDT',
        status: 'APPROVED',
        requestedBy: 'admin',
        checkerComment: 'Title verified and matches beneficiary. Approved.',
      },
    ],
  },

  // TXN-DEMO-016: IBFT - SUCCESS after TWO amendments (1 rejected, 1 approved)
  {
    id: 'TXN-DEMO-016',
    mtoName: 'Flutterwave Nigeria',
    mtoRef: 'MTO-DEMO-016',
    bankRef: 'BNK-DEMO-016',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    amountFCY: '1800.00',
    currencyFCY: 'EUR',
    amountPKR: '1179000',
    corridor: 'SN-FR',
    purposeCode: 'EDU',
    purposeText: 'Education',
    currentState: 'SUCCESS',
    lifecycleStatus: 'SUCCESS',
    createdAt: new Date(Date.now() - 345600000).toISOString(),
    updatedAt: new Date(Date.now() - 259200000).toISOString(),
    remitterName: 'Samuel Mensah',
    beneficiaryName: 'MICHAEL BROWN',
    beneficiaryIBAN: 'FR7630006000011234567890192',
    beneficiaryBankName: 'Ecobank Uganda',
    amendAllowed: false,
    cancelAllowed: false,
    cancellationAction: 'CANNOT_CANCEL',
    amendStatus: 'APPROVED',
    cancelStatus: 'NONE',
    railRefs: {
      RaastMsgId: 'RAAST-MSG-016',
      UETR: 'UETR-016',
    },
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 340000000).toISOString(),
        primaryRef: 'SCR-016',
        durationMs: 1300,
        attempts: 1,
      },
      {
        stateCode: 'RAAST_TITLE_FETCH',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 338000000).toISOString(),
        primaryRef: 'RAAST-TF-016-FAIL',
        failReasonCode: 'TITLE_MISMATCH',
        durationMs: 1100,
        attempts: 2,
      },
      {
        stateCode: 'RAAST_TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(Date.now() - 280000000).toISOString(),
        primaryRef: 'RAAST-TF-016-PASS',
        durationMs: 900,
        attempts: 1,
      },
      {
        stateCode: 'RAAST_POST',
        status: 'PASS',
        timestamp: new Date(Date.now() - 275000000).toISOString(),
        primaryRef: 'RAAST-MSG-016',
        durationMs: 2800,
        attempts: 1,
      },
      {
        stateCode: 'SUCCESS',
        status: 'PASS',
        timestamp: new Date(Date.now() - 259200000).toISOString(),
        primaryRef: 'EPRC-016',
        durationMs: 90,
        attempts: 1,
      },
    ],
    ePRCNo: 'EPRC-DEMO-016',
    ePRCDate: new Date(Date.now() - 259200000).toISOString(),
    amendmentHistory: [
      {
        timestamp: new Date(Date.now() - 330000000).toISOString(),
        oldAccount: 'FR7630006000011234567890193',
        newAccount: 'FR7630006000011234567890194',
        fetchedTitle: 'MICHAEL B',
        status: 'REJECTED',
        requestedBy: 'admin',
        checkerComment:
          'Title is truncated. Cannot verify identity. Please retry with correct account.',
      },
      {
        timestamp: new Date(Date.now() - 300000000).toISOString(),
        oldAccount: 'FR7630006000011234567890193',
        newAccount: 'FR7630006000011234567890192',
        fetchedTitle: 'MICHAEL BROWN',
        status: 'APPROVED',
        requestedBy: 'admin',
        checkerComment: 'Title matches. Account verified successfully.',
      },
    ],
  },

  // TXN-DEMO-017: FT - RTGS manually marked as COMPLETED (success) with comment
  {
    id: 'TXN-DEMO-017',
    mtoName: 'M-Pesa Kenya',
    mtoRef: 'MTO-DEMO-017',
    bankRef: 'BNK-DEMO-017',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    amountFCY: '10000.00',
    currencyFCY: 'EUR',
    amountPKR: '1550000',
    corridor: 'KE-DE',
    purposeCode: 'SAL',
    purposeText: 'Business Payment',
    currentState: 'RTGS_POST',
    lifecycleStatus: 'SUCCESS',
    createdAt: new Date(Date.now() - 432000000).toISOString(),
    updatedAt: new Date(Date.now() - 345600000).toISOString(),
    remitterName: 'Tsitsi Mwangi',
    beneficiaryName: 'Berlin Steel Works Ltd',
    beneficiaryIBAN: 'DE89370400440532013003',
    amendAllowed: false,
    cancelAllowed: false,
    cancellationAction: 'CANNOT_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {
      UETR: 'UETR-RTGS-017',
    },
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 430000000).toISOString(),
        primaryRef: 'SCR-017',
        durationMs: 1400,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(Date.now() - 428000000).toISOString(),
        primaryRef: 'TF-017',
        durationMs: 850,
        attempts: 1,
      },
      {
        stateCode: 'RTGS_BALANCE_CHECK',
        status: 'PASS',
        timestamp: new Date(Date.now() - 426000000).toISOString(),
        primaryRef: 'RTGS-BC-017',
        durationMs: 600,
        attempts: 1,
      },
      {
        stateCode: 'RTGS_POST',
        status: 'PASS',
        timestamp: new Date(Date.now() - 345600000).toISOString(),
        primaryRef: 'RTGS-017',
        durationMs: 5000,
        attempts: 1,
      },
    ],
    ePRCNo: 'EPRC-DEMO-017',
    ePRCDate: new Date(Date.now() - 345600000).toISOString(),
    manualActionComment: {
      actionType: 'MANUAL_COMPLETE',
      comment:
        'Confirmed with RTGS operations team. Funds credited to beneficiary account DE89370400440532013003. Bank confirmation ref: RTGS-CONF-2025-0417.',
      timestamp: new Date(Date.now() - 345600000).toISOString(),
    },
  },

  // TXN-DEMO-018: FT - RTGS manually marked as FAILED with comment
  {
    id: 'TXN-DEMO-018',
    mtoName: 'Paga Ghana',
    mtoRef: 'MTO-DEMO-018',
    bankRef: 'BNK-DEMO-018',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    amountFCY: '7500.00',
    currencyFCY: 'USD',
    amountPKR: '11250000',
    corridor: 'NG-US',
    purposeCode: 'MED',
    purposeText: 'Medical',
    currentState: 'RTGS_POST',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 518400000).toISOString(),
    updatedAt: new Date(Date.now() - 432000000).toISOString(),
    remitterName: 'Thato Malatsi',
    beneficiaryName: 'Mayo Clinic',
    beneficiaryIBAN: 'US021000021123456793',
    amendAllowed: false,
    cancelAllowed: true,
    cancellationAction: 'REVERSE_FINANCIAL_ACTION',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {
      UETR: 'UETR-RTGS-018',
    },
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 516000000).toISOString(),
        primaryRef: 'SCR-018',
        durationMs: 1200,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(Date.now() - 514000000).toISOString(),
        primaryRef: 'TF-018',
        durationMs: 900,
        attempts: 1,
      },
      {
        stateCode: 'RTGS_BALANCE_CHECK',
        status: 'PASS',
        timestamp: new Date(Date.now() - 512000000).toISOString(),
        primaryRef: 'RTGS-BC-018',
        durationMs: 550,
        attempts: 1,
      },
      {
        stateCode: 'RTGS_POST',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 432000000).toISOString(),
        primaryRef: 'RTGS-018',
        failReasonCode: 'BENEFICIARY_BANK_REJECTED',
        durationMs: 8000,
        attempts: 2,
      },
    ],
    manualActionComment: {
      actionType: 'MANUAL_FAIL',
      comment:
        'Beneficiary bank rejected the RTGS transfer. Reason: Account frozen due to regulatory hold. Contacted beneficiary bank ops desk - confirmed account is under investigation. Transaction cannot proceed.',
      timestamp: new Date(Date.now() - 430000000).toISOString(),
    },
  },

  // TXN-DEMO-019: IBFT - Amendment approved + manual complete comment (full journey)
  {
    id: 'TXN-DEMO-019',
    mtoName: 'GTBank Ghana',
    mtoRef: 'MTO-DEMO-019',
    bankRef: 'BNK-DEMO-019',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    amountFCY: '3200.00',
    currencyFCY: 'GBP',
    amountPKR: '48000',
    corridor: 'GH-UK',
    purposeCode: 'FAM',
    purposeText: 'Personal Remittance',
    currentState: 'SUCCESS',
    lifecycleStatus: 'SUCCESS',
    createdAt: new Date(Date.now() - 604800000).toISOString(),
    updatedAt: new Date(Date.now() - 518400000).toISOString(),
    remitterName: 'Kwame Asante',
    beneficiaryName: 'ANNA WILLIAMS',
    beneficiaryIBAN: 'GB29NWBK60161331926825',
    beneficiaryBankName: 'Ecobank Togo',
    amendAllowed: false,
    cancelAllowed: false,
    cancellationAction: 'CANNOT_CANCEL',
    amendStatus: 'APPROVED',
    cancelStatus: 'NONE',
    railRefs: {
      RaastMsgId: 'RAAST-MSG-019',
      UETR: 'UETR-019',
    },
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 600000000).toISOString(),
        primaryRef: 'SCR-019',
        durationMs: 1100,
        attempts: 1,
      },
      {
        stateCode: 'RAAST_TITLE_FETCH',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 598000000).toISOString(),
        primaryRef: 'RAAST-TF-019-FAIL',
        failReasonCode: 'ACCOUNT_CLOSED',
        durationMs: 1200,
        attempts: 1,
      },
      {
        stateCode: 'RAAST_TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(Date.now() - 540000000).toISOString(),
        primaryRef: 'RAAST-TF-019-PASS',
        durationMs: 800,
        attempts: 1,
      },
      {
        stateCode: 'RAAST_POST',
        status: 'PASS',
        timestamp: new Date(Date.now() - 535000000).toISOString(),
        primaryRef: 'RAAST-MSG-019',
        durationMs: 3100,
        attempts: 1,
      },
      {
        stateCode: 'SUCCESS',
        status: 'PASS',
        timestamp: new Date(Date.now() - 518400000).toISOString(),
        primaryRef: 'EPRC-019',
        durationMs: 100,
        attempts: 1,
      },
    ],
    ePRCNo: 'EPRC-DEMO-019',
    ePRCDate: new Date(Date.now() - 518400000).toISOString(),
    amendmentHistory: [
      {
        timestamp: new Date(Date.now() - 580000000).toISOString(),
        oldAccount: 'GB29NWBK60161331926826',
        newAccount: 'GB29NWBK60161331926825',
        fetchedTitle: 'ANNA WILLIAMS',
        status: 'APPROVED',
        requestedBy: 'admin',
        checkerComment:
          'Original account was closed. New account title matches beneficiary. Approved for reprocessing.',
      },
    ],
    manualActionComment: {
      actionType: 'MANUAL_COMPLETE',
      comment:
        'Transaction reprocessed after account amendment. Confirmation received. Funds successfully credited.',
      timestamp: new Date(Date.now() - 520000000).toISOString(),
    },
  },

  // ===== CHECKER INBOX AMENDMENT TRANSACTIONS =====
  // These transactions appear in both the main Transactions table AND the Checker Amendment Inbox

  {
    id: 'TXN-30000',
    mtoName: 'GTBank Ghana',
    mtoRef: 'MTO-30000',
    bankRef: 'BNK-30000',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    amountFCY: '1000.00',
    currencyFCY: 'GBP',
    amountPKR: '15000',
    corridor: 'GH-UK',
    purposeCode: 'FAM',
    purposeText: 'Personal Remittance',
    currentState: 'TITLE_FETCH',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 7200000).toISOString(),
    updatedAt: new Date(Date.now() - 3600000).toISOString(),
    remitterName: 'Adekunle Okoro',
    beneficiaryName: 'ROBERT TAYLOR',
    beneficiaryIBAN: 'GB29NWBK60161331926827',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'PENDING',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 6000000).toISOString(),
        primaryRef: 'SCR-30000',
        durationMs: 1200,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 3600000).toISOString(),
        primaryRef: 'TF-30000',
        failReasonCode: 'INVALID_ACCOUNT',
        durationMs: 900,
        attempts: 2,
      },
    ],
    amendmentHistory: [
      {
        timestamp: new Date(Date.now() - 3600000).toISOString(),
        oldAccount: 'GB29NWBK60161331926827',
        newAccount: 'GB29NWBK60161331926828',
        fetchedTitle: 'ROBERT TAYLOR',
        status: 'PENDING',
        requestedBy: 'Admin User',
      },
    ],
  },

  {
    id: 'TXN-30001',
    mtoName: 'Paga Ghana',
    mtoRef: 'MTO-30001',
    bankRef: 'BNK-30001',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    amountFCY: '1100.00',
    currencyFCY: 'USD',
    amountPKR: '1650000',
    corridor: 'NG-US',
    purposeCode: 'SAL',
    purposeText: 'Business Payment',
    currentState: 'TITLE_FETCH',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 10800000).toISOString(),
    updatedAt: new Date(Date.now() - 7200000).toISOString(),
    remitterName: 'Fatoumata Ndiaye',
    beneficiaryName: 'JAMES SMITH',
    beneficiaryIBAN: 'US021000021123456794',
    beneficiaryBankName: "Ecobank Côte d'Ivoire",
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'PENDING',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 9000000).toISOString(),
        primaryRef: 'SCR-30001',
        durationMs: 1100,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 7200000).toISOString(),
        primaryRef: 'TF-30001',
        failReasonCode: 'TITLE_MISMATCH',
        durationMs: 800,
        attempts: 1,
      },
    ],
    amendmentHistory: [
      {
        timestamp: new Date(Date.now() - 7200000).toISOString(),
        oldAccount: 'US021000021123456794',
        newAccount: 'US021000021123456795',
        fetchedTitle: 'JAMES SMITH',
        status: 'PENDING',
        requestedBy: 'Maker User 1',
      },
    ],
  },

  {
    id: 'TXN-30002',
    mtoName: 'Flutterwave Nigeria',
    mtoRef: 'MTO-30002',
    bankRef: 'BNK-30002',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    amountFCY: '1200.00',
    currencyFCY: 'EUR',
    amountPKR: '786000',
    corridor: 'SN-FR',
    purposeCode: 'EDU',
    purposeText: 'Education',
    currentState: 'TITLE_FETCH',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 14400000).toISOString(),
    updatedAt: new Date(Date.now() - 10800000).toISOString(),
    remitterName: 'Grace Kipchoge',
    beneficiaryName: 'JOHN DOE',
    beneficiaryIBAN: 'FR7630006000011234567890195',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'PENDING',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 13000000).toISOString(),
        primaryRef: 'SCR-30002',
        durationMs: 1300,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 10800000).toISOString(),
        primaryRef: 'TF-30002',
        failReasonCode: 'INVALID_ACCOUNT',
        durationMs: 1000,
        attempts: 3,
      },
    ],
    amendmentHistory: [
      {
        timestamp: new Date(Date.now() - 10800000).toISOString(),
        oldAccount: 'FR7630006000011234567890195',
        newAccount: 'FR7630006000011234567890196',
        fetchedTitle: 'JOHN DOE',
        status: 'PENDING',
        requestedBy: 'Maker User 2',
      },
    ],
  },

  {
    id: 'TXN-30003',
    mtoName: 'GTBank Ghana',
    mtoRef: 'MTO-30003',
    bankRef: 'BNK-30003',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    amountFCY: '1300.00',
    currencyFCY: 'GBP',
    amountPKR: '19500',
    corridor: 'GH-UK',
    purposeCode: 'MED',
    purposeText: 'Medical',
    currentState: 'TITLE_FETCH',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 18000000).toISOString(),
    updatedAt: new Date(Date.now() - 14400000).toISOString(),
    remitterName: 'Amara Jalloh',
    beneficiaryName: 'SAMUEL MENSAH',
    beneficiaryIBAN: 'GB29NWBK60161331926829',
    beneficiaryBankName: 'Stanbic Bank Ghana',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'PENDING',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 17000000).toISOString(),
        primaryRef: 'SCR-30003',
        durationMs: 1000,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 14400000).toISOString(),
        primaryRef: 'TF-30003',
        failReasonCode: 'ACCOUNT_DORMANT',
        durationMs: 750,
        attempts: 1,
      },
    ],
    amendmentHistory: [
      {
        timestamp: new Date(Date.now() - 14400000).toISOString(),
        oldAccount: 'GB29NWBK60161331926829',
        newAccount: 'GB29NWBK60161331926830',
        fetchedTitle: 'SAMUEL MENSAH',
        status: 'PENDING',
        requestedBy: 'Admin User',
      },
    ],
  },

  {
    id: 'TXN-30004',
    mtoName: 'Paga Ghana',
    mtoRef: 'MTO-30004',
    bankRef: 'BNK-30004',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    amountFCY: '1400.00',
    currencyFCY: 'USD',
    amountPKR: '2100000',
    corridor: 'NG-US',
    purposeCode: 'FAM',
    purposeText: 'Personal Remittance',
    currentState: 'TITLE_FETCH',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 21600000).toISOString(),
    updatedAt: new Date(Date.now() - 18000000).toISOString(),
    remitterName: 'Chinedu Eze',
    beneficiaryName: 'DAVID JOHNSON',
    beneficiaryIBAN: 'US021000021123456796',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'PENDING',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 20000000).toISOString(),
        primaryRef: 'SCR-30004',
        durationMs: 1200,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 18000000).toISOString(),
        primaryRef: 'TF-30004',
        failReasonCode: 'INVALID_ACCOUNT',
        durationMs: 850,
        attempts: 2,
      },
    ],
    amendmentHistory: [
      {
        timestamp: new Date(Date.now() - 18000000).toISOString(),
        oldAccount: 'US021000021123456796',
        newAccount: 'US021000021123456797',
        fetchedTitle: 'DAVID JOHNSON',
        status: 'PENDING',
        requestedBy: 'Maker User 2',
      },
    ],
  },

  // ===== 1LINK TIMEOUT TRANSACTIONS =====
  {
    id: 'TXN-DEMO-030',
    mtoName: 'Flutterwave Nigeria',
    mtoRef: 'MTO-DEMO-030',
    bankRef: 'BNK-DEMO-030',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    amountFCY: '1350.00',
    currencyFCY: 'EUR',
    amountPKR: '884250',
    corridor: 'SN-FR',
    purposeCode: 'FAM',
    purposeText: 'Personal Remittance',
    currentState: 'ONELINK_POST',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 4800000).toISOString(),
    updatedAt: new Date(Date.now() - 2400000).toISOString(),
    remitterName: 'Samuel Mensah',
    beneficiaryName: 'MARIE DUPONT',
    beneficiaryIBAN: 'FR7630006000011234567890197',
    beneficiaryBankName: 'Access Bank Nigeria',
    amendAllowed: false,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 4500000).toISOString(),
        primaryRef: 'SCR-DEMO-030',
        durationMs: 1100,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(Date.now() - 4200000).toISOString(),
        primaryRef: 'TF-DEMO-030',
        durationMs: 800,
        attempts: 1,
      },
      {
        stateCode: 'ONELINK_POST',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 2400000).toISOString(),
        primaryRef: '1LINK-DEMO-030',
        failReasonCode: 'ONELINK_TIMEOUT',
        durationMs: 30000,
        attempts: 2,
      },
    ],
  },
  {
    id: 'TXN-DEMO-031',
    mtoName: 'M-Pesa Kenya',
    mtoRef: 'MTO-DEMO-031',
    bankRef: 'BNK-DEMO-031',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    amountFCY: '890.00',
    currencyFCY: 'EUR',
    amountPKR: '137950',
    corridor: 'KE-DE',
    purposeCode: 'SAL',
    purposeText: 'Business Payment',
    currentState: 'ONELINK_POST',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 9600000).toISOString(),
    updatedAt: new Date(Date.now() - 7200000).toISOString(),
    remitterName: 'Tsitsi Mwangi',
    beneficiaryName: 'KLAUS SCHMIDT',
    beneficiaryIBAN: 'DE89370400440532013004',
    beneficiaryBankName: 'KCB Kenya',
    amendAllowed: false,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 9300000).toISOString(),
        primaryRef: 'SCR-DEMO-031',
        durationMs: 1200,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(Date.now() - 9000000).toISOString(),
        primaryRef: 'TF-DEMO-031',
        durationMs: 750,
        attempts: 1,
      },
      {
        stateCode: 'ONELINK_POST',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 7200000).toISOString(),
        primaryRef: '1LINK-DEMO-031',
        failReasonCode: 'ONELINK_TIMEOUT',
        durationMs: 30000,
        attempts: 3,
      },
    ],
  },
  {
    id: 'TXN-DEMO-032',
    mtoName: 'GTBank Ghana',
    mtoRef: 'MTO-DEMO-032',
    bankRef: 'BNK-DEMO-032',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    amountFCY: '2100.00',
    currencyFCY: 'GBP',
    amountPKR: '31500',
    corridor: 'GH-UK',
    purposeCode: 'MED',
    purposeText: 'Medical',
    currentState: 'ONELINK_POST',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 14400000).toISOString(),
    updatedAt: new Date(Date.now() - 10800000).toISOString(),
    remitterName: 'Thato Malatsi',
    beneficiaryName: 'MICHAEL BROWN',
    beneficiaryIBAN: 'GB29NWBK60161331926831',
    beneficiaryBankName: 'Ecobank Ghana',
    amendAllowed: false,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 14000000).toISOString(),
        primaryRef: 'SCR-DEMO-032',
        durationMs: 1000,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(Date.now() - 13500000).toISOString(),
        primaryRef: 'TF-DEMO-032',
        durationMs: 900,
        attempts: 1,
      },
      {
        stateCode: 'ONELINK_POST',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 10800000).toISOString(),
        primaryRef: '1LINK-DEMO-032',
        failReasonCode: 'ONELINK_TIMEOUT',
        durationMs: 30000,
        attempts: 1,
      },
    ],
  },

  // ===== ADDITIONAL SCREENING TRANSACTIONS =====
  {
    id: 'TXN-DEMO-033',
    mtoName: 'Paga Ghana',
    mtoRef: 'MTO-DEMO-033',
    bankRef: 'BNK-DEMO-033',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    amountFCY: '450.00',
    currencyFCY: 'USD',
    amountPKR: '675000',
    corridor: 'NG-US',
    purposeCode: 'SAL',
    purposeText: 'Business Payment',
    currentState: 'SCREENING',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 6000000).toISOString(),
    updatedAt: new Date(Date.now() - 3000000).toISOString(),
    remitterName: 'Kwame Asante',
    beneficiaryName: 'ANNA WILLIAMS',
    beneficiaryIBAN: 'US021000021123456798',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 3000000).toISOString(),
        primaryRef: 'SCR-DEMO-033',
        failReasonCode: 'SCREENING_TIMEOUT',
        durationMs: 15000,
        attempts: 2,
      },
    ],
  },
  {
    id: 'TXN-DEMO-034',
    mtoName: 'Flutterwave Nigeria',
    mtoRef: 'MTO-DEMO-034',
    bankRef: 'BNK-DEMO-034',
    branchCode: 'SN-DKR-001',
    agentCode: 'FLW-NG-001',
    payoutType: 'OTC',
    amountFCY: '670.00',
    currencyFCY: 'EUR',
    amountPKR: '438850',
    corridor: 'SN-FR',
    purposeCode: 'FAM',
    purposeText: 'Personal Remittance',
    currentState: 'SCREENING',
    lifecycleStatus: 'IN_PROGRESS',
    createdAt: new Date(Date.now() - 3600000).toISOString(),
    updatedAt: new Date(Date.now() - 1800000).toISOString(),
    remitterName: 'Adekunle Okoro',
    beneficiaryName: 'ROBERT TAYLOR',
    beneficiaryIBAN: 'FR7630006000011234567890198',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PENDING',
        timestamp: new Date(Date.now() - 1800000).toISOString(),
        primaryRef: 'SCR-DEMO-034',
        durationMs: 0,
        attempts: 1,
      },
    ],
  },

  // ===== ADDITIONAL BALANCE CHECK TRANSACTIONS =====
  {
    id: 'TXN-DEMO-035',
    mtoName: 'M-Pesa Kenya',
    mtoRef: 'MTO-DEMO-035',
    bankRef: 'BNK-DEMO-035',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    amountFCY: '1800.00',
    currencyFCY: 'EUR',
    amountPKR: '279000',
    corridor: 'KE-DE',
    purposeCode: 'EDU',
    purposeText: 'Education',
    currentState: 'BALANCE_CHECK',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 10800000).toISOString(),
    updatedAt: new Date(Date.now() - 7200000).toISOString(),
    remitterName: 'Fatoumata Ndiaye',
    beneficiaryName: 'JAMES SMITH',
    beneficiaryIBAN: 'DE89370400440532013005',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 10000000).toISOString(),
        primaryRef: 'SCR-DEMO-035',
        durationMs: 1100,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(Date.now() - 9500000).toISOString(),
        primaryRef: 'TF-DEMO-035',
        durationMs: 800,
        attempts: 1,
      },
      {
        stateCode: 'BALANCE_CHECK',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 7200000).toISOString(),
        primaryRef: 'BC-DEMO-035',
        failReasonCode: 'INSUFFICIENT_BALANCE',
        durationMs: 500,
        attempts: 1,
      },
    ],
  },
  {
    id: 'TXN-DEMO-036',
    mtoName: 'GTBank Ghana',
    mtoRef: 'MTO-DEMO-036',
    bankRef: 'BNK-DEMO-036',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    amountFCY: '920.00',
    currencyFCY: 'GBP',
    amountPKR: '13800',
    corridor: 'GH-UK',
    purposeCode: 'MED',
    purposeText: 'Medical',
    currentState: 'BALANCE_CHECK',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 7200000).toISOString(),
    updatedAt: new Date(Date.now() - 5400000).toISOString(),
    remitterName: 'Grace Kipchoge',
    beneficiaryName: 'JOHN DOE',
    beneficiaryIBAN: 'GB29NWBK60161331926832',
    beneficiaryBankName: 'Ecobank Nigeria',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 6800000).toISOString(),
        primaryRef: 'SCR-DEMO-036',
        durationMs: 1000,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(Date.now() - 6400000).toISOString(),
        primaryRef: 'TF-DEMO-036',
        durationMs: 850,
        attempts: 1,
      },
      {
        stateCode: 'BALANCE_CHECK',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 5400000).toISOString(),
        primaryRef: 'BC-DEMO-036',
        failReasonCode: 'BALANCE_CHECK_TIMEOUT',
        durationMs: 20000,
        attempts: 2,
      },
    ],
  },

  // ===== MANUAL SETTLEMENT (DUPLICATE TXN) TRANSACTIONS =====
  {
    id: 'TXN-DEMO-040',
    mtoName: 'Flutterwave Nigeria',
    mtoRef: 'MTO-DEMO-040',
    bankRef: 'BNK-DEMO-040',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    amountFCY: '1500.00',
    currencyFCY: 'EUR',
    amountPKR: '982500',
    corridor: 'SN-FR',
    purposeCode: 'FAM',
    purposeText: 'Personal Remittance',
    currentState: 'ONELINK_POST',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 86400000).toISOString(),
    updatedAt: new Date(Date.now() - 43200000).toISOString(),
    remitterName: 'Amara Jalloh',
    beneficiaryName: 'SAMUEL MENSAH',
    beneficiaryIBAN: 'FR7630006000011234567890199',
    beneficiaryBankName: 'Ecobank Senegal',
    amendAllowed: false,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 86000000).toISOString(),
        primaryRef: 'SCR-DEMO-040',
        durationMs: 1200,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(Date.now() - 85000000).toISOString(),
        primaryRef: 'TF-DEMO-040',
        durationMs: 800,
        attempts: 1,
      },
      {
        stateCode: 'ONELINK_POST',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 43200000).toISOString(),
        primaryRef: '1LINK-DEMO-040',
        failReasonCode: 'DUPLICATE_TXN',
        durationMs: 1500,
        attempts: 1,
      },
    ],
  },
  {
    id: 'TXN-DEMO-041',
    mtoName: 'M-Pesa Kenya',
    mtoRef: 'MTO-DEMO-041',
    bankRef: 'BNK-DEMO-041',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    amountFCY: '2200.00',
    currencyFCY: 'EUR',
    amountPKR: '341000',
    corridor: 'KE-DE',
    purposeCode: 'SAL',
    purposeText: 'Business Payment',
    currentState: 'ONELINK_POST',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 172800000).toISOString(),
    updatedAt: new Date(Date.now() - 86400000).toISOString(),
    remitterName: 'Chinedu Eze',
    beneficiaryName: 'DAVID JOHNSON',
    beneficiaryIBAN: 'DE89370400440532013006',
    beneficiaryBankName: 'Ecobank Uganda',
    amendAllowed: false,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 172000000).toISOString(),
        primaryRef: 'SCR-DEMO-041',
        durationMs: 1100,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(Date.now() - 171000000).toISOString(),
        primaryRef: 'TF-DEMO-041',
        durationMs: 900,
        attempts: 1,
      },
      {
        stateCode: 'ONELINK_POST',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 86400000).toISOString(),
        primaryRef: '1LINK-DEMO-041',
        failReasonCode: 'DUPLICATE_TXN',
        durationMs: 1200,
        attempts: 1,
      },
    ],
  },
  {
    id: 'TXN-DEMO-042',
    mtoName: 'GTBank Ghana',
    mtoRef: 'MTO-DEMO-042',
    bankRef: 'BNK-DEMO-042',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    amountFCY: '780.00',
    currencyFCY: 'GBP',
    amountPKR: '11700',
    corridor: 'GH-UK',
    purposeCode: 'MED',
    purposeText: 'Medical',
    currentState: 'ONELINK_POST',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 259200000).toISOString(),
    updatedAt: new Date(Date.now() - 172800000).toISOString(),
    remitterName: 'Samuel Mensah',
    beneficiaryName: 'MARIE DUPONT',
    beneficiaryIBAN: 'GB29NWBK60161331926833',
    beneficiaryBankName: 'Ecobank Togo',
    amendAllowed: false,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 258000000).toISOString(),
        primaryRef: 'SCR-DEMO-042',
        durationMs: 1300,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(Date.now() - 257000000).toISOString(),
        primaryRef: 'TF-DEMO-042',
        durationMs: 850,
        attempts: 1,
      },
      {
        stateCode: 'ONELINK_POST',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 172800000).toISOString(),
        primaryRef: '1LINK-DEMO-042',
        failReasonCode: 'DUPLICATE_TXN',
        durationMs: 1400,
        attempts: 1,
      },
    ],
  },
  {
    id: 'TXN-DEMO-043',
    mtoName: 'Paga Ghana',
    mtoRef: 'MTO-DEMO-043',
    bankRef: 'BNK-DEMO-043',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    amountFCY: '3100.00',
    currencyFCY: 'USD',
    amountPKR: '4650000',
    corridor: 'NG-US',
    purposeCode: 'EDU',
    purposeText: 'Education',
    currentState: 'ONELINK_POST',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 345600000).toISOString(),
    updatedAt: new Date(Date.now() - 259200000).toISOString(),
    remitterName: 'Tsitsi Mwangi',
    beneficiaryName: 'KLAUS SCHMIDT',
    beneficiaryIBAN: 'US021000021123456799',
    beneficiaryBankName: "Ecobank Côte d'Ivoire",
    amendAllowed: false,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 344000000).toISOString(),
        primaryRef: 'SCR-DEMO-043',
        durationMs: 1000,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(Date.now() - 343000000).toISOString(),
        primaryRef: 'TF-DEMO-043',
        durationMs: 750,
        attempts: 1,
      },
      {
        stateCode: 'ONELINK_POST',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 259200000).toISOString(),
        primaryRef: '1LINK-DEMO-043',
        failReasonCode: 'DUPLICATE_TXN',
        durationMs: 1600,
        attempts: 1,
      },
    ],
  },
  {
    id: 'TXN-DEMO-044',
    mtoName: 'Flutterwave Nigeria',
    mtoRef: 'MTO-DEMO-044',
    bankRef: 'BNK-DEMO-044',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    amountFCY: '1250.00',
    currencyFCY: 'EUR',
    amountPKR: '818750',
    corridor: 'SN-FR',
    purposeCode: 'FAM',
    purposeText: 'Personal Remittance',
    currentState: 'SUCCESS',
    lifecycleStatus: 'SUCCESS',
    createdAt: new Date(Date.now() - 604800000).toISOString(),
    updatedAt: new Date(Date.now() - 432000000).toISOString(),
    remitterName: 'Thato Malatsi',
    beneficiaryName: 'MICHAEL BROWN',
    beneficiaryIBAN: 'FR7630006000011234567890200',
    beneficiaryBankName: 'Stanbic Bank Ghana',
    amendAllowed: false,
    cancelAllowed: false,
    cancellationAction: 'CANNOT_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 600000000).toISOString(),
        primaryRef: 'SCR-DEMO-044',
        durationMs: 1100,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(Date.now() - 590000000).toISOString(),
        primaryRef: 'TF-DEMO-044',
        durationMs: 800,
        attempts: 1,
      },
      {
        stateCode: 'ONELINK_POST',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 518400000).toISOString(),
        primaryRef: '1LINK-DEMO-044',
        failReasonCode: 'DUPLICATE_TXN',
        durationMs: 1300,
        attempts: 1,
      },
      {
        stateCode: 'SUCCESS',
        status: 'PASS',
        timestamp: new Date(Date.now() - 432000000).toISOString(),
        primaryRef: 'MANUAL-SETTLE-044',
        durationMs: 100,
        attempts: 1,
      },
    ],
    manualSettlementHistory: [
      {
        action: 'SUBMITTED',
        reason:
          'Confirmed with 1Link ops team that transaction was processed successfully despite duplicate error response.',
        by: 'Maker User 1',
        at: new Date(Date.now() - 500000000).toISOString(),
      },
      {
        action: 'APPROVED',
        reason: 'Verified 1Link settlement report. Funds were credited.',
        by: 'Checker User 1',
        at: new Date(Date.now() - 432000000).toISOString(),
      },
    ],
  },
  {
    id: 'TXN-DEMO-045',
    mtoName: 'M-Pesa Kenya',
    mtoRef: 'MTO-DEMO-045',
    bankRef: 'BNK-DEMO-045',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    amountFCY: '950.00',
    currencyFCY: 'EUR',
    amountPKR: '147250',
    corridor: 'KE-DE',
    purposeCode: 'SAL',
    purposeText: 'Business Payment',
    currentState: 'ONELINK_POST',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 864000000).toISOString(),
    updatedAt: new Date(Date.now() - 691200000).toISOString(),
    remitterName: 'Kwame Asante',
    beneficiaryName: 'ANNA WILLIAMS',
    beneficiaryIBAN: 'DE89370400440532013007',
    beneficiaryBankName: 'Access Bank Nigeria',
    amendAllowed: false,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 860000000).toISOString(),
        primaryRef: 'SCR-DEMO-045',
        durationMs: 1200,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(Date.now() - 850000000).toISOString(),
        primaryRef: 'TF-DEMO-045',
        durationMs: 750,
        attempts: 1,
      },
      {
        stateCode: 'ONELINK_POST',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 777600000).toISOString(),
        primaryRef: '1LINK-DEMO-045',
        failReasonCode: 'DUPLICATE_TXN',
        durationMs: 1400,
        attempts: 1,
      },
    ],
    manualSettlementHistory: [
      {
        action: 'SUBMITTED',
        reason: 'Customer claims funds were received. Please settle.',
        by: 'Maker User 2',
        at: new Date(Date.now() - 750000000).toISOString(),
      },
      {
        action: 'REJECTED',
        reason:
          'Cannot settle based on customer claim alone. Need official 1Link confirmation report.',
        by: 'Checker User 1',
        at: new Date(Date.now() - 691200000).toISOString(),
      },
    ],
  },
]
